import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { executiveSummary, coreValues } from '../data/content';

const About = () => {
  return (
    <div className="min-h-screen bg-white pt-32 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6" style={{ color: '#5E7F8A' }}>
            About Paix Family Mediation
          </h1>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: '#9BB2A2' }}></div>
        </div>

        {/* Image */}
        <div className="mb-16">
          <img 
            src="https://customer-assets.emergentagent.com/job_e8a7a8e1-af64-416b-ba2f-55c511897d70/artifacts/xz3afjnk_About%20photo.png" 
            alt="Professional mediation session"
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
        </div>

        {/* Executive Summary */}
        <div className="mb-16">
          <div className="rounded-lg p-10 text-white mb-8" style={{ backgroundColor: '#5E7F8A' }}>
            <h2 className="text-3xl font-bold text-center">Our Mission</h2>
          </div>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#5E7F8A' }}>
            {executiveSummary}
          </p>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#5E7F8A' }}>
            What Guides Us
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="p-6 rounded-lg border-l-4 transition-all hover:shadow-md"
                style={{ borderLeftColor: '#9BB2A2' }}
              >
                <p className="text-lg font-medium" style={{ color: '#5E7F8A' }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Brand Voice Section */}
        <div className="mb-16">
          <div className="rounded-lg p-10 text-white mb-8" style={{ backgroundColor: '#5E7F8A' }}>
            <h2 className="text-3xl font-bold text-center">Our Approach</h2>
          </div>
          <p className="text-lg leading-relaxed" style={{ color: '#5E7F8A' }}>
            We lead with emotional awareness, focusing on calm, non adversarial communication and emphasizing respect, privacy, and well being. This is different from the litigation approach to family disputes, which focus on "winning" and getting the upper hand over the other party. We know that divorce can be one of the most challenging transitions for a family, and we want to help you navigate this transition with dignity.
          </p>
        </div>

        {/* Meet Your Mediator Section */}
        <div className="mb-16">
          <div className="rounded-lg p-10 text-white mb-8" style={{ backgroundColor: '#5E7F8A' }}>
            <h2 className="text-3xl font-bold text-center">Meet Your Mediator</h2>
          </div>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-1/3">
              <img 
                src="https://customer-assets.emergentagent.com/job_2b8b53d6-f420-42d5-9708-89a0fcd9ca22/artifacts/7dwb187p_replicate-prediction-ja0csvspjnrmw0cwqgnv951y3w.jpeg" 
                alt="Jeanine Zalduendo"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div className="md:w-2/3">
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#5E7F8A' }}>
                Jeanine Zalduendo
              </h3>
              <p className="text-lg font-medium mb-4" style={{ color: '#9BB2A2' }}>
                Mediator | Harvard-Trained Attorney | Advocate for Families in Transition
              </p>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5E7F8A' }}>
                Some people find their way to mediation through a career path. I found mine through life itself. I am a Harvard Law School graduate and a commercial litigator with more than twenty years of experience navigating some of the most complex, high-stakes disputes in the country.
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: '#5E7F8A' }}>
                Over time, I came to understand something important: winning in court rarely means healing. It rarely means peace. For families caught in the upheaval of divorce or separation, the courtroom can leave everyone — especially children — more wounded than when they walked in.
              </p>
              <Link 
                to="/biography"
                className="inline-flex items-center font-semibold transition-opacity hover:opacity-80"
                style={{ color: '#9BB2A2' }}
                data-testid="read-full-bio"
              >
                Read Full Biography
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gray-50 rounded-lg p-10 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#5E7F8A' }}>
            Ready to Take the Next Step?
          </h2>
          <p className="text-lg mb-6" style={{ color: '#5E7F8A' }}>
            Schedule a free consultation to discuss how mediation can help your family.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 rounded-md text-white font-semibold transition-all hover:shadow-lg"
            style={{ backgroundColor: '#9BB2A2' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#8FA596'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9BB2A2'}
            data-testid="contact-cta"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
